import React, { useEffect } from 'react';
import { GoogleAuthProvider, signInWithPopup, signInWithRedirect } from 'firebase/auth';
import { auth } from '../../backend/firebase';
import { useAppDispatch, useAppSelector } from '../../redux/hooks';
import { dismissUpgradePrompt, selectShowUpgradePrompt } from '../../redux/features/auth/slice';
import { persistor } from '../../redux/store';
import { track } from '../../helpers/analytics';
import { signInWithApple } from '../../helpers/appleSignIn';
import { t } from '../../i18n';
import './SignInModal.scss';
import { UPGRADE_DISMISSED_KEY } from './constants';

const provider = new GoogleAuthProvider();

const SignInModal = () => {
    const dispatch = useAppDispatch();
    const showUpgradePrompt = useAppSelector(selectShowUpgradePrompt);

    // Analytics: fire upgrade_prompt_shown when the prompt becomes visible
    useEffect(() => {
        if (showUpgradePrompt) {
            track('upgrade_prompt_shown');
        }
    }, [showUpgradePrompt]);

    if (!showUpgradePrompt) return null;

    const handleSignIn = async () => {
        track('sign_in_started', { trigger: 'upgrade_prompt', provider: 'google' });
        try {
            await signInWithPopup(auth, provider);
            // AuthProvider.tsx handles the rest (tile upgrade + setUid)
        } catch (err: any) {
            // Popup blocked or closed — fall back to redirect (handles iOS Safari)
            if (err.code === 'auth/popup-blocked' || err.code === 'auth/popup-closed-by-user') {
                // Flush redux-persist to localStorage before navigating away so
                // anonymousTileIds survive the page reload caused by signInWithRedirect.
                await persistor.flush();
                await signInWithRedirect(auth, provider);
            } else {
                console.error('Sign-in error:', err);
            }
        }
    };

    const handleAppleSignIn = async () => {
        track('sign_in_started', { trigger: 'upgrade_prompt', provider: 'apple' });
        // signInWithApple mirrors the Google popup→redirect fallback; AuthProvider
        // reacts to the auth state change, so the success path (tile upgrade +
        // setUid + assigned color) is identical to Google.
        await signInWithApple();
    };

    const handleDismiss = () => {
        // Suppress for the rest of this browser session
        sessionStorage.setItem(UPGRADE_DISMISSED_KEY, '1');
        dispatch(dismissUpgradePrompt());
    };

    return (
        <div className="sign-in-modal">
            <div className="sign-in-modal__content">
                <p className="sign-in-modal__message">
                    {t('web.auth.upgradePromptMessage')}
                </p>
                <button className="sign-in-modal__btn-primary" onClick={handleSignIn}>
                    {t('web.auth.signInWithGoogle')}
                </button>
                <button className="sign-in-modal__btn-apple" onClick={handleAppleSignIn}>
                    <svg
                        className="sign-in-modal__apple-logo"
                        viewBox="0 0 384 512"
                        width="16"
                        height="16"
                        aria-hidden="true"
                        focusable="false"
                    >
                        <path
                            fill="currentColor"
                            d="M318.7 268.7c-.2-36.7 16.4-64.4 50-84.8-18.8-26.9-47.2-41.7-84.7-44.6-35.5-2.8-74.3 20.7-88.5 20.7-15 0-49.4-19.7-76.4-19.7C63.3 141.2 4 184.8 4 273.5q0 39.3 14.4 81.2c12.8 36.7 59 126.7 107.2 125.2 25.2-.6 43-17.9 75.8-17.9 31.8 0 48.3 17.9 76.4 17.9 48.6-.7 90.4-82.5 102.6-119.3-65.2-30.7-61.7-90-61.7-91.9zm-56.6-164.2c27.3-32.4 24.8-61.9 24-72.5-24.1 1.4-52 16.4-67.9 34.9-17.5 19.8-27.8 44.3-25.6 71.9 26.1 2 49.9-11.4 69.5-34.3z"
                        />
                    </svg>
                    {t('web.auth.continueWithApple')}
                </button>
                <button className="sign-in-modal__btn-dismiss" onClick={handleDismiss}>
                    {t('web.auth.maybeLater')}
                </button>
            </div>
        </div>
    );
};

export default SignInModal;
